import type { ReactElement } from 'react';
import * as React from 'react';
import { Button, Link, Modal, Text } from '@hotjar/hotsauce';
import personSitsOnTable from '@hotjar/shared-assets/images/person-sits-on-table.svg';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import styled from '@emotion/styled';
import { CaptionLink, HighlightBox, HighlightBoxImage, ModalBody, Subtitle } from './styles';

const TCBody = styled.div`
  margin-top: 48px;
`;

export const EngageTCModal = ({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (result: boolean) => void;
}): ReactElement => {
  const { t } = useTranslationWithPrefix('auth:engage:tc-modal');
  const helpHotjarUrl = useHelpHotjarUrl();

  return (
    <Modal contentLabel={t('title')} isOpen={isOpen} onClose={onClose} size="l">
      <Modal.Header title={t('title')} />
      <ModalBody>
        <HighlightBox textRightMargin={80}>
          <div className="text-part">
            <Subtitle>{t('subtitle')}</Subtitle>
            <Text as="p" textType="caption" textColor="text_light">
              {t('message')}
            </Text>
            <CaptionLink
              href={`${helpHotjarUrl}/articles/10158131341463`}
              kind="standalone"
              isExternal
            >
              {t('link')}
            </CaptionLink>
          </div>
          <div className="image-part">
            <HighlightBoxImage src={personSitsOnTable} />
          </div>
        </HighlightBox>
        <TCBody>
          <Text as="h1" textType="header4">
            Terms and Conditions Engage
          </Text>
          <Text as="p" textType="body1">
            Last updated: 27 February 2023
          </Text>
          <Text as="p" textType="body1">
            <strong>
              PingPong is now operating under the brand Hotjar Engage. Any references in these Terms
              and Conditions and supporting documentation to PingPong should from the publication
              date of this contract apply equally to Hotjar Engage.{' '}
            </strong>
          </Text>
          <Text as="p" textType="body1">
            Please read these Terms and Conditions, carefully before registering for a subscription
            for the Services offered on this website operated by PingPong UX Limited (
            <strong>“PingPong”</strong>) with its registered office at C/O Craufurd Hale Group
            Ground Floor, Arena Court, Crown Lane, Maidenhead, United Kingdom, SL6 8QZ, England,
            registered company number, 10551060, VAT number GB261287208.
          </Text>
          <Text as="p" textType="body1">
            By signing up for the Services at https://insights.hotjar.com/register (previously
            hellopingpong.com) and accepting PingPong’s Terms and Conditions, Data Processing
            Agreement (DPA) and Privacy Policy (the <strong>“Agreement”</strong>), You agree to be
            legally bound by this Agreement as may be modified and posted on our website from time
            to time. In the event of any inconsistency between the content of the Terms and
            Conditions, DPA and the Privacy Policy, the Terms and Conditions shall prevail followed
            by the DPA and then the Privacy Policy.
          </Text>
          <Text as="p" textType="body1">
            If You do not wish to be bound by these Terms and Conditions, DPA and Privacy Policy
            then You may not register to use the Trial Period or purchase our Services.
          </Text>
          <Text as="p" textType="subtitle1">
            1. Definitions
          </Text>
          <Text as="p" textType="body1">
            In this Agreement, the following words shall have the following meanings:
          </Text>
          <strong>“Confidential Information”</strong>
          <Text as="p" textType="body1">
            means any and all information in whatsoever form relating to PingPong, You, Internal
            Testers, External Testers or the business, prospective business, finances, technical
            processes, computer Software (both source code and object code), Intellectual Property
            Rights or finances of PingPong or You (as the case may be) which comes into a party’s
            possession by virtue of its entry into this Agreement or provision of the Services, and
            which the party regards, or could reasonably be expected to regard, as confidential;
          </Text>
          <strong>“DPA”</strong>
          <Text as="p" textType="body1">
            means the data processing agreement of PingPong published{' '}
            <Link kind="inline" as="a" isExternal href="http://hellopingpong.com/legal/dpa">
              on our website
            </Link>{' '}
            as amended from time to time;
          </Text>
          <strong>“Effective Date”</strong>
          <Text as="p" textType="body1">
            means the date on which payment for the Order is accepted by PingPong issuing an
            invoice;
          </Text>
          <strong>“External Testers”</strong>
          <Text as="p" textType="body1">
            means Your own users, sourced by You to participate in a particular research session
            offered as part of our Services;
          </Text>
          <strong>“Fees”</strong>
          <Text as="p" textType="body1">
            means the fees payable for the Services included in each Order at the rates published on
            our website as amended from time to time;
          </Text>
          <strong>“Internal Tester”</strong>
          <Text as="p" textType="body1">
            means a user who has registered a tester account with PingPong to take part in research;
          </Text>
          <strong>“Intellectual Property Rights”</strong>
          <Text as="p" textType="body1">
            shall mean copyrights, rights to use, and trademarks, property rights, patents, patent
            applications, trade secrets, moral rights, know-how, trade names, service marks, logos,
            domain names and any other industrial or intellectual property rights recognised in
            England or any other jurisdiction; in each case whether registered or unregistered,
            which currently subsist, or will subsist, now or in the future, in any part of the
            world;{' '}
          </Text>
          <strong>“Order”</strong>
          <Text as="p" textType="body1">
            means the online order form completed by You, each time You sign up to our Services;
          </Text>
          <strong>“Personal Data“</strong>
          <Text as="p" textType="body1">
            means any data that can be used to identify an individual, whether directly or
            indirectly (e.g. name, identification number, location data, an online identifier,
            etc.). This definition includes any equivalent definition in the Applicable Law;
          </Text>
          <strong>“Privacy Policy”</strong>
          <Text as="p" textType="body1">
            means the privacy policy of PingPong published{' '}
            <Link kind="inline" as="a" isExternal href="http://hellopingpong.com/legal/dpa">
              on our website
            </Link>{' '}
            as amended from time to time;
          </Text>
          <strong>“Services”</strong>
          <Text as="p" textType="body1">
            means the Software application services of PingPong described in this Agreement,
            including any computer Software programmes and, if appropriate, any updates;
          </Text>
          <strong>“Software”</strong>
          <Text as="p" textType="body1">
            shall mean the Software as a service (SaaS) offered by PingPong consisting of a variety
            of analytics and user feedback products;
          </Text>
          <strong>“Term”</strong>
          <Text as="p" textType="body1">
            means the period starting on the Effective Date until the Agreement is terminated in
            accordance with the rights of the parties set out in this Agreement;
          </Text>
          <strong>“Terms and Conditions”</strong>
          <Text as="p" textType="body1">
            means these terms and conditions of PingPong published{' '}
            <Link kind="inline" as="a" isExternal href="http://hellopingpong.com/legal/dpa">
              on our website
            </Link>{' '}
            as amended from time to time;
          </Text>
          <strong>“Trial Period”</strong>
          <Text as="p" textType="body1">
            means free access provided to You by PingPong for a limited and established period of
            time, to the Software;
          </Text>
          <strong>“You” / “Your”</strong>
          <Text as="p" textType="body1">
            means the company or person who completes the Order;
          </Text>
          <strong>“Your Data”</strong>
          <Text as="p" textType="body1">
            means all data that You post, link, store, share and otherwise make available within or
            via the Services for the purpose of using the Services or facilitating Your use of the
            Services, including but not limited to, any Personal Data or Confidential Information,
            in particular all information, text, graphics, videos or other material and details of
            External Testers.{' '}
          </Text>
          <Text as="p" textType="subtitle1">
            2. Services
          </Text>
          <Text as="p" textType="body1">
            2.1 PingPong provides a research product that facilitates user interviews and user tests
            between You (<strong>“Researcher”</strong>) and Internal Testers or External Testers.
            The platform enables You to create new research with Internal Testers or External
            Testers by defining the length of a session, the number of Internal Testers or External
            Testers, the demographic settings and creating the screening survey. Based on Your
            settings the platform allows you to invite External Testers for Your research or invites
            Internal Testers from its pool to participate in Your sessions. The sessions are
            provided via the platform where the Internal Tester or External Testers can share their
            screen, video camera and microphone. Sessions are automatically recorded on the platform
            and can be watched remotely by users authorized by You.
          </Text>
          <Text as="p" textType="subtitle1">
            3. Testers{' '}
          </Text>
          <Text as="p" textType="subtitle1">
            3.1. Internal Testers
          </Text>
          <Text as="p" textType="body1">
            3.1.1 Internal Testers are private individuals who have signed up with PingPong and
            indicated that they are willing to participate in research. Internal Testers are not
            PingPong’s employees, agents or representatives. PingPong shall have no liability to You
            whatsoever for the acts or omissions of Internal Testers, including in particular any
            use of Your Confidential Information that You make available to them as a result of this
            Agreement.
          </Text>
          <Text as="p" textType="body1">
            3.1.2 Internal Testers are automatically compensated by PingPong at the end of a session
            for taking part in a session with You.
          </Text>
          <Text as="p" textType="body1">
            3.1.3 You may only communicate with Internal Testers via the Services unless You have an
            existing relationship with the Internal Tester before inviting them to participate in a
            session.
          </Text>
          <Text as="p" textType="body1">
            3.1.4 You undertake not to solicit or employ any Internal Tester with whom You have had
            a session, within 12 months of the most recent session with such Internal Tester. Any
            breach of this section shall be deemed a material breach of this Agreement and could
            also result in termination of Your account. You hereby permit PingPong to review
            communications made between You and any Internal Tester to check compliance with this
            provision.
          </Text>
          <Text as="p" textType="subtitle1">
            3.2. External Testers
          </Text>
          <Text as="p" textType="body1">
            3.2.1. External Testers are sourced by You, and may be Your internal personnel, or any
            external person who has signed up to open a tester account via a unique link generated
            by You to participate in Your research project via our Services.{' '}
          </Text>
          <Text as="p" textType="body1">
            3.2.2. You may use the Services to: (i) recruit Your own External Testers; (ii) run
            research with External Testers; (iii) create research, schedule, screen and run sessions
            with External Testers.{' '}
          </Text>
          <Text as="p" textType="body1">
            3.2.3. If You request, PingPong will transfer Personal Data of the External Testers to
            You in a structured format.
          </Text>
          <Text as="p" textType="body1">
            3.2.4. PingPong acts as the data processor of the Personal Data of the External Testers
            and You are the controller and owner of the Personal Data of External Testers. You shall
            be solely and exclusively responsible for the lawfulness of the processing of External
            Testers Personal Data and other protected information.{' '}
            <strong>
              This includes obtaining any consent or other applicable legal basis that You are
              legally or contractually required to obtain.
            </strong>
          </Text>
          <Text as="p" textType="body1">
            3.2.5. No customers of PingPong other than You, have access to Your External
            Testers&apos; data. PingPong shall have no liability to You whatsoever for the acts or
            omissions of External Testers, including in particular any use of Your Confidential
            Information that You make available to them as a result of this Agreement.
          </Text>
          <Text as="p" textType="body1">
            3.2.6. You shall be wholly responsible for the recruitment of all such External Testers
            used in any session and PingPong shall have no liability whatsoever for any transactions
            between You and such External Testers.{' '}
          </Text>
          <Text as="p" textType="body1">
            3.2.7. Unless You order additional services from PingPong, PingPong is not responsible
            for the compensation of External Testers. Any potential compensation of the External
            Testers remains Your sole responsibility and PingPong will not be liable for any
            compensation-related claims.
          </Text>
          <Text as="p" textType="subtitle1">
            4. License
          </Text>
          <Text as="p" textType="body1">
            4.1 Except as otherwise stated herein, all rights, titles, and interest in our site
            and/or the Software and any content and Intellectual Property Rights contained therein
            are the exclusive property of PingPong. Unless otherwise specified, the Software is for
            Your limited use only and if You copy or download any information from this site and/or
            Software, You agree that You shall not remove or obscure any copyright or other notices
            contained in any such information.
          </Text>
          <Text as="p" textType="body1">
            4.2 Subject to this Agreement, PingPong grants You a limited, revocable, non-exclusive,
            non-transferable and non-assignable license to use the site and/or access the Software
            as a service (SaaS) solution for commercial use subject to the terms of this Agreement
            for the duration of the Term and solely for the purposes of receiving the Services. You
            hereby agree not to resell any part of the Software to third parties. You may not
            modify, copy, distribute, or otherwise use our site and/or the Software in any other way
            as set out in this section. You shall not use the Software, including in conjunction
            with, any device, program, or service designed to circumvent any deployed technological
            measures, in an attempt to control access to, or the rights in, a content file or other
            work protected by intellectual property laws.
          </Text>
          <Text as="p" textType="body1">
            4.3. Any breach of the above shall constitute a material breach of this Agreement and
            shall prompt PingPong, at our own discretion, to immediately suspend or terminate Your
            right to access the Services. Any breach of the above shall make You liable for damages
            suffered by PingPong.
          </Text>
          <Text as="p" textType="body1">
            4.4 You shall retain sole ownership of all rights, title and interest in and to Your
            Data and Your pre-existing Intellectual Property Rights. You grant PingPong a
            non-exclusive license to use Your Data and Intellectual Property Rights and any third
            party owned item from the Term to the extent required for the provision of the Services.
            PingPong is granted a non-transferable license to view any research and video data
            contained or created by the Services (including any of Your associated Software,
            Intellectual Property Rights and Confidential Information) for the purposes of complying
            with its obligations under this Agreement, for example for support, debugging or
            operational purposes.
          </Text>

          <Text as="p" textType="body1">
            4.5 You acknowledge and agree not to share any videos of sessions created via use of the
            Services publicly or for marketing purposes or any other purpose not specifically
            permitted by this Agreement. Videos may only be shared with Your own team only for the
            purpose of Your own internal market research or product/service improvement.
          </Text>

          <Text as="p" textType="body1">
            4.6 PingPong may take and maintain technical precautions to review Your use of the
            Services, in order to provide the Services to You, to evaluate its provision of the
            Services and to improve its service offerings.
          </Text>
          <Text as="p" textType="subtitle1">
            5. Fees
          </Text>
          <Text as="p" textType="body1">
            5.1 We offer different subscription plans for our Software (the{' '}
            <strong>“Subscription Plan”</strong>).{' '}
          </Text>
          <Text as="p" textType="body1">
            5.2 Fees are based upon the number of sessions and their length, or based on Your chosen{' '}
            <Link kind="inline" as="a" isExternal href="https://www.hotjar.com/pricing/">
              Subscription Plan
            </Link>
            .
          </Text>
          <Text as="p" textType="body1">
            5.3 All Fees are exclusive of VAT, that may be applicable in Your jurisdiction.{' '}
          </Text>
          <Text as="p" textType="body1">
            5.4. Information about our Subscription Plans can be found{' '}
            <Link kind="inline" as="a" isExternal href="http://hellopingpong.com/legal/dpa">
              on our site
            </Link>
            . For additional information on our Subscription Plans, please{' '}
            <Link kind="inline" as="a" isExternal href={`${helpHotjarUrl}/requests/new`}>
              contact us
            </Link>
            . You may upgrade or downgrade Your Subscription at any time during Your subscription
            term, upon which PingPong will apply the respective fees on a pro-rata basis.
          </Text>
          <Text as="p" textType="body1">
            5.4 Trial Period: At the end of the Trial Period, You will be prompted to enter Your
            payment details if You have not already done so. If You&apos;ve already updated Your
            payment method You will be automatically charged on the date shown on the billing
            section associated with Your Account.
          </Text>
          <Text as="p" textType="body1">
            5.5 Renewal Term: Unless You upgrade or downgrade Your Subscription Plan, You will be
            enrolled into an automatic renewing cycle for the same term at the conclusion of the
            Subscription Term (the <strong>“Renewal Term”</strong>). This applies to all
            Subscription Plans involving payment including monthly and annual renewals.{' '}
          </Text>
          <Text as="p" textType="subtitle1">
            6. Invoicing and Payment
          </Text>
          <Text as="p" textType="body1">
            6.1 Unless otherwise agreed in writing, PingPong shall invoice You in respect of the
            Fees automatically by charging all fees to Your credit card, debit card or other valid
            payment method used starting from the Effective Date, on a monthly or annual basis, as
            set out in the Order or Subscription Plan. Your payment obligations are non-cancelable
            and no payments are refundable other than for any canceled sessions as set out below.
          </Text>
          <Text as="p" textType="body1">
            6.2 All invoices are payable in full in the currency set out in the invoice together
            with any Value Added Tax (if applicable).
          </Text>
          <Text as="p" textType="body1">
            6.3 You undertake that all details provided for the purpose of obtaining the Services
            will be correct and that the payment details used are Your own and that there are
            sufficient funds or credit facilities to cover the Fees.
          </Text>
          <Text as="p" textType="subtitle1">
            7. Cancellation of Sessions and Refunds
          </Text>
          <Text as="p" textType="body1">
            7.1 You may cancel any scheduled sessions and will be credited with Fees paid for
            canceled sessions unless: (i) You cancel a scheduled session within 24 hours before the
            session starts, then Your account will be credited with 50% of the Fees for each
            cancelled session; or (ii) You cancel any session within 8 hours before the session
            starts, no credit for the Fees paid will be made to Your Account.
          </Text>
          <Text as="p" textType="body1">
            7.2 Where an Internal Tester does not attend a scheduled session for any reason, the
            Fees paid for the scheduled session will be credited in full to Your account.
          </Text>
          <Text as="p" textType="body1">
            7.3 If You (or a user authorized by You) fail to attend a session, no Fees relating to
            the session will be refunded or credited to You.
          </Text>
          <Text as="p" textType="body1">
            7.4 Where PingPong is unable to provide any Internal Testers for Your research request,
            You shall be refunded all Fees paid which remain unused on Your account, provided that
            You request a refund within 30 calendar days of being notified by PingPong that no
            participants have been found for Your research request. No refunds apply to Your use of
            External Testers via our Services.
          </Text>
          <Text as="p" textType="body1">
            7.5. This section shall only apply to purchased credits that can be used to pay for our
            Services and does not apply to the Services provided based on the Subscription Plan.{' '}
          </Text>
          <Text as="p" textType="subtitle1">
            8. Warranties and Disclaimers
          </Text>
          <Text as="p" textType="body1">
            8.1 You agree and acknowledge that Internal Testers are neither employees nor agents of
            PingPong, but are independent contractors who use the platform to provide services to
            You by participating in sessions. All information or content provided by Internal
            Testers via the Services is provided &quot;as is&quot;. PingPong provides no warranties
            in relation to such content or information and shall have no liability whatsoever to You
            for Your use or reliance upon such content or information of Internal Testers nor for
            the acts or omissions of any Internal Testers.
          </Text>
          <Text as="p" textType="body1">
            8.2 The Services allow You to post, link, store, share and otherwise make available Your
            Data in text, graphics, videos or other material and You warrant and represent that: (i)
            You shall have the sole responsibility for the legality, reliability, integrity,
            accuracy and quality of Your Data; (ii) You rightfully own all right, title and interest
            in and to Your Data and have the right to use Your Data and grant PingPong, its
            employees, agents and subcontractors the right to use Your Data for the purposes set out
            in this Agreement; (iii) any use of Your Data by PingPong, its employees, agents,
            subprocessors and subcontractors with or via the Services shall not breach any rights of
            a third party.
          </Text>
          <Text as="p" textType="body1">
            8.3 You warrant and represent that You shall maintain reasonable security measures (as
            may change over time) covering, without limitation, confidentiality, authenticity and
            integrity to ensure that the access to the Services granted under this Agreement is
            limited as set out under this Agreement.
          </Text>
          <Text as="p" textType="body1">
            8.4 Except as expressly stated in this Agreement, all warranties and conditions, whether
            express or implied by statute, common law or otherwise (including but not limited to
            satisfactory quality and fitness for purpose), are hereby excluded to the fullest extent
            permitted by law. No warranty is made regarding the results of usage of the Services or
            that the functionality of the Services will meet Your requirements or that the Services
            will operate uninterrupted or error free. This section shall survive the termination of
            this Agreement.
          </Text>
          <Text as="p" textType="subtitle1">
            9. Liability
          </Text>
          <Text as="p" textType="body1">
            9.1. In no event shall PingPong or any of its representatives be liable to You or any
            third party for any direct, indirect, special, exemplary, punitive or other
            consequential or incidental damages (including but not limited to any lost profits or
            revenue, interruption, loss of programs or other information, or any other measurable
            loss) arising directly or indirectly from:{' '}
          </Text>
          <ol type="a">
            <Text as="li">
              Your use of or access to our site and/or Software, or any content, products or
              Services distributed on or provided through our site and/or Services,
            </Text>
            <Text as="li">or any failure or interruption of our site and/or Software; </Text>
          </ol>
          <Text as="p" textType="body1">
            whether arising out of errors, omissions, loss of data, defects, viruses, interruptions
            or delays in operations or transmission or any other cause, whether based on warranty,
            contract, tort (including negligence) or any other legal theory, even if PingPong or its
            suppliers have been expressly advised of the possibility of such damages. This
            limitation of liability does not apply to direct damages for which PingPong is liable
            due to PingPong’s violation of a third party’s Intellectual Property Rights.{' '}
          </Text>
          <Text as="p" textType="body1">
            9.2. In any event, PingPong&apos;s total maximum aggregate liability under this
            Agreement shall not exceed the total Fees (excluding any VAT, duty, sales or similar
            taxes) paid or to be paid by You to PingPong during the preceding twelve (12) month
            period or, if the duration of the Agreement has been less than twelve (12) months, such
            shorter period, as applicable.
          </Text>
          <Text as="p" textType="body1">
            9.3 PingPong shall not be liable for any breaches of this Agreement caused by the acts,
            omissions or negligence of any Internal Testers and External Testers. In no event shall
            You raise any claim under this Agreement more than one (1) year after: (i) the discovery
            of the circumstances giving rise to such claim; or (ii) the effective date of the
            termination of this Agreement. This section shall survive the termination of this
            Agreement.
          </Text>
          <Text as="p" textType="body1">
            9.4 You acknowledge and agree that in entering into this Agreement, You had recourse to
            Your own skill and judgment and have not relied on any representations made by PingPong,
            or any of its employees or agents.
          </Text>
          <Text as="p" textType="subtitle1">
            10. Indemnification
          </Text>
          <Text as="p" textType="body1">
            10.1. Our Indemnification Obligations: PingPong agrees to defend You against any claim,
            suit or proceeding brought by third parties (the <strong>“Claim(s)”</strong>) arising
            from or relating to Our violation of a third party’s Intellectual Property Rights
            directly arising out of Your use of the Software in accordance this Agreement, and will
            indemnify and hold You harmless against any damages and costs awarded against You or
            agreed in settlement by PingPong (including reasonable attorneys’ fees) resulting from
            such Claim. PingPong shall have no indemnification obligation with respect to any Claims
            arising out of or related to (including but not limited to):
          </Text>
          <ol type="a">
            <Text as="li">Your Data; </Text>
            <Text as="li">Your violation of any Applicable Laws; </Text>
            <Text as="li">
              Your violation, whether alleged or actual, of any third party rights, including but
              not limited to data protection and privacy rights;{' '}
            </Text>
            <Text as="li">Your violation of this Agreement; </Text>
            <Text as="li">Your negligence, willful misconduct, or fraud; </Text>
            <Text as="li">
              Your failure to use any enhancements, modifications, or updates to the Software that
              have been provided by PingPong;
            </Text>
            <Text as="li">modifications to the Software by anyone other than PingPong; or </Text>
            <Text as="li">
              combinations of the Software with Software, data or materials not provided by
              PingPong.
            </Text>
          </ol>
          <Text as="p" textType="body1">
            10.2. Your Indemnification Obligations: You shall indemnify, hold harmless and defend
            PingPong, including any of its subsidiaries, officers, owners, partners, directors,
            employees, contractors, agents, shareholders, licensors, suppliers and other partners (
            <strong>“PingPong Indemnified Parties”</strong>), to the maximum extent permitted and in
            full at Your own cost, from any demands, disputes, liabilities, Claims, obligations,
            losses, damages, and costs and expenses, including, without limitation, reasonable legal
            and accounting fees arising out of or in any way connected with (including but not
            limited to):
          </Text>
          <ol type="a">
            <Text as="li">
              Your unauthorized use of any material obtained through our site and Software;{' '}
            </Text>
            <Text as="li">
              Use by PingPong of Your Data or External Testers’ data in providing the Services
            </Text>
            <Text as="li">
              Your use and access to our site and Software which is not in accordance with this
              Agreement;{' '}
            </Text>
            <Text as="li">
              Your Processing of Personal Data in connection with Your use of our Software;{' '}
            </Text>
            <Text as="li">Your violation of the Agreement; and </Text>
            <Text as="li">
              Your violation, whether alleged or actual, of any third party rights.
            </Text>
          </ol>
          <Text as="p" textType="body1">
            10.3. Indemnification Procedures: The parties’ respective indemnification obligations
            above are conditioned on:
          </Text>
          <ol type="a">
            <Text as="li">
              the indemnified parties giving the indemnifying party prompt written notice of the
              claim, except that the failure to provide prompt notice will only limit the
              indemnification obligations to the extent the indemnifying party is prejudiced by the
              delay or failure;{' '}
            </Text>
            <Text as="li">
              the indemnifying party has full and complete control over the defense and settlement
              of the claim (if permitted by applicable law) in particular, the indemnified party
              shall not settle or make other binding declarations concerning the third party claim
              without the prior written approval of the other party;{' '}
            </Text>
            <Text as="li">
              the relevant indemnified parties providing assistance in connection with the defense
              and settlement of the claim (as long as the settlement does not include any payment of
              any amounts by or any admissions of liability, whether civil or criminal, on the part
              of any of the indemnified parties), as the indemnifying party may reasonably request;
              and{' '}
            </Text>
            <Text as="li">
              the indemnified parties’ compliance with any settlement or court order made in
              connection with the Claim arising under this clause and settled by the indemnifying
              party or with its approval.
            </Text>
          </ol>
          <Text as="p" textType="body1">
            10.4. Infringement Remedy: If You are enjoined or otherwise prohibited from using any of
            the Software or a portion thereof based on a third party Intellectual Property
            infringement claim covered by our indemnification obligations under this Clause above,
            then PingPong will, at our sole expense and option, either:{' '}
          </Text>
          <ol type="a">
            <Text as="li">
              obtain for You the right to use the allegedly infringing portions of the Software;
            </Text>
            <Text as="li">
              modify the allegedly infringing portions of the Software so as to render them
              non-infringing without substantially diminishing or impairing their functionality; or
            </Text>
            <Text as="li">
              replace the allegedly infringing portions of the Software with non-infringing items of
              substantially similar functionality. If PingPong determines that the foregoing
              remedies are not commercially reasonable, then PingPong will promptly provide a
              prorated refund to You for any prepaid Fees received by PingPong under this Agreement
              that correspond to the unused portion of the subscription term. The remedy set out in
              this Clause is Your sole and exclusive remedy for any actual or alleged infringement
              by Us of any third party Intellectual Property Rights in the event that You are
              enjoined or otherwise prohibited from using any of the Software or a portion thereof
              based on a claim covered by our indemnification obligations under this Clause. This
              limitation shall not apply in the case of PingPong’s willful misconduct.
            </Text>
          </ol>
          <Text as="p" textType="body1">
            10.5. You shall not, without the prior express written approval of PingPong, attempt to,
            or settle, dispose or enter into any proposed settlement or resolution of any Claim
            (whether having been finally adjudicated or otherwise) brought against You, if such
            settlement or resolution results in any obligation or liability for PingPong. This
            clause shall survive termination of this Agreement, howsoever occurred, and termination
            of Your access and/or use of our site or Software.
          </Text>
          <Text as="p" textType="subtitle1">
            11. Termination
          </Text>
          <Text as="p" textType="body1">
            11.1 PingPong may immediately terminate this Agreement or the provision of any Services
            provided pursuant to this Agreement if: (i) You have used or permitted the use of the
            Services otherwise than in accordance with this Agreement; or (ii) PingPong is
            prohibited, under the laws of England or otherwise, from providing the Services.
          </Text>
          <Text as="p" textType="body1">
            11.2 Either party shall be entitled to terminate this Agreement on written notice to the
            other party if the other party: (i) goes into voluntary or involuntary liquidation
            (otherwise than for the purpose of a solvent reconstruction or amalgamation) or has a
            receiver or administrator or similar person appointed or is unable to pay its debts
            within the meaning of s268 Insolvency Act 1986 or ceases or threatens to cease to carry
            on business or if any event occurs which is analogous to any of the foregoing in another
            jurisdiction; or (ii) commits a material breach of any term of this Agreement which, if
            capable of remedy, is not remedied within five (5) Business Days of receipt of a written
            notice specifying the breach and requiring it to be remedied; or (iii) is prevented by
            an event of force majeure from fulfilling its obligations for more than twenty eight
            (28) business days.
          </Text>
          <Text as="p" textType="body1">
            11.3 Upon termination of this Agreement PingPong shall: (i) immediately cease providing
            the Services to You and all licences granted hereunder shall terminate. No Fees already
            paid shall be refunded; (ii) at Your option, following receipt of a request from You
            delete (in accordance with the terms of the DPA) or return all of Your Data stored in
            PingPong’s database in a machine readable format, free of charge, provided that such
            request is made within thirty (30) calendar days of termination. If You require any of
            Your Data to be returned in a different format PingPong reserves the right to charge for
            this additional service on a time and materials basis.
          </Text>
          <Text as="p" textType="body1">
            11.4 Termination of this Agreement for whatever reason shall not affect the accrued
            rights of the parties. All clauses which by their nature should continue after
            termination shall, for the avoidance of doubt, survive the expiration or sooner
            termination of this Agreement and shall remain in force and effect.
          </Text>
          <Text as="p" textType="subtitle1">
            12. Confidential Information
          </Text>
          <Text as="p" textType="body1">
            12.1 Each party may use the Confidential Information of a disclosing party only for the
            purposes of this Agreement and must keep confidential all Confidential Information of
            each disclosing party except to the extent (if any) the recipient of any Confidential
            Information is required by law to disclose the Confidential Information.
          </Text>
          <Text as="p" textType="body1">
            12.2 Either party may disclose the Confidential Information of the other party to those
            of its employees and agents who have a need to know the Confidential Information for the
            purposes of this Agreement but only if the employee or agent executes a confidentiality
            undertaking.
          </Text>
          <Text as="p" textType="body1">
            12.3 Both parties agree to return (or destroy at the request of the disclosing party)
            all documents, materials or data containing Confidential Information to the disclosing
            party without delay upon completion of the Services or termination or expiry of this
            Agreement.
          </Text>
          <Text as="p" textType="body1">
            12.4 The obligations of confidentiality under this Agreement do not extend to
            information that: (i) was rightfully in the possession of the receiving party before the
            negotiations leading to this Agreement; (ii) is, or after the Effective Date of this
            Agreement, becomes public knowledge (otherwise than as a result of a breach of this
            Agreement); or (iii) is lawfully disclosed to the receiving party by a third party
            without restriction on disclosure; or (iv) is required to be disclosed by law, by any
            court of competent jurisdiction or by any regulatory or administrative body.
          </Text>
          <Text as="p" textType="body1">
            12.5 The parties acknowledge and agree that without prejudice to the general
            confidentiality provisions in this section and without limitation, all information
            falling within the definition of Confidential Information as set out in this Agreement
            and any information which is supplied by the disclosing party to the receiving party
            pursuant to this Agreement or the negotiation thereof is: (i) Confidential Information
            the disclosure of which by the receiving party would be an actionable breach of
            confidence; or (ii) a trade secret of the disclosing party; and (iii) information, the
            disclosure of which would be likely to prejudice the commercial interests of the
            disclosing party or of any other person.
          </Text>
          <Text as="p" textType="subtitle1">
            13. Data Protection
          </Text>
          <Text as="p" textType="body1">
            13.1 Each party undertakes to comply with its obligations under relevant applicable data
            protection laws, principles and agreements.
          </Text>
          <Text as="p" textType="body1">
            13.2 To the extent that personal data is processed when You or Your users use the
            Services, the parties acknowledge that PingPong is a data processor and You are a data
            controller and the parties shall comply with their respective obligations under
            applicable data protection law and the terms of the DPA.
          </Text>
          <Text as="p" textType="body1">
            13.3 If a third party alleges infringement of its data protection rights, PingPong shall
            be entitled to take measures necessary to prevent the infringement of a third party’s
            rights from continuing.
          </Text>
          <Text as="p" textType="body1">
            13.4 Where PingPong collects and processes personal data, as a data controller, when
            providing the Services to You, for example when You or any users provide an email
            address upon registration or when You order Services, such collection and processing
            shall be in accordance with the{' '}
            <Link
              kind="inline"
              as="a"
              isExternal
              href="https://www.hellopingpong.com/legal/privacy-for-researchers"
            >
              Privacy Policy
            </Link>
            .
          </Text>
          <Text as="p" textType="subtitle1">
            14. Miscellaneous
          </Text>
          <Text as="p" textType="body1">
            14.1 Should a provision of this Agreement be invalid or become invalid then the legal
            effect of the other provisions shall be unaffected. A valid provision is deemed to have
            been agreed which comes closest to what the parties intended commercially and shall
            replace the invalid provision. The same shall apply to any omissions.
          </Text>
          <Text as="p" textType="body1">
            14.2 This Agreement constitutes the whole agreement and understanding between the
            parties and supersedes all prior agreements, representations, negotiations and
            discussions between the parties relating to the subject matter thereof.
          </Text>
          <Text as="p" textType="body1">
            14.3 Both parties may only assign their rights and obligations under the Agreement in
            connection with a consolidation, merger, acquisition or sale of substantially all of
            their assets, shares or activities without the prior written consent of the other party.
          </Text>
          <Text as="p" textType="body1">
            14.4 PingPong and You are independent contractors and nothing in this Agreement will be
            construed as creating an employer-employee relationship.
          </Text>
          <Text as="p" textType="body1">
            14.5 Amendments to the Agreement shall be in writing (which includes email) and all
            notices shall be sent via email. Notices about amendments to the Agreements shall be
            deemed to have been duly given if posted by PingPong as a written notice at{' '}
            <Link kind="inline" as="a" isExternal href="http://www.hellopingpong.com/legal">
              www.hellopingpong.com/legal
            </Link>
            , and if sent to You to the email address linked to Your account. All changes shall be
            deemed to have been accepted by You unless You terminate the Agreement prior to the
            expiry of the 30 calendar days period, provided such changes are not mandated by law to
            take effect on an earlier date. Your use of our site and/or Software following the
            notification date of any modifications to this Agreement will also constitute Your
            acceptance of the modified Agreement.
          </Text>
          <Text as="p" textType="body1">
            14.6 Nothing contained in this Agreement is intended to be enforceable pursuant to the
            Contracts (Rights of Third Parties) Act 1999, or any similar legislation in any
            applicable jurisdiction.
          </Text>
          <Text as="p" textType="body1">
            14.7 This Agreement shall be governed by the laws of England and Wales. The courts of
            England shall have exclusive jurisdiction for the settlement of all disputes arising
            under this Agreement.
          </Text>
        </TCBody>
      </ModalBody>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button kind="primary" onClick={() => onSubmit(true)}>
          Accept terms and continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
